import { useState, useCallback, useRef, RefObject, useEffect } from 'react';

interface UseCustomScrollbarsProps {
  headerContainerRef: RefObject<HTMLDivElement>;
  dataScrollAreaRef: RefObject<HTMLDivElement>;
  fixedColumnWidth: number;
}

/**
 * カスタムスクロールバー用の統合フック
 */
export const useCustomScrollbars = ({
  headerContainerRef,
  dataScrollAreaRef,
  fixedColumnWidth
}: UseCustomScrollbarsProps) => {
  // ========== 共通のスクロール管理 ==========
  const isScrollingRef = useRef(false);

  /**
   * スクロール同期関数（横方向）
   */
  const syncScroll = useCallback((sourceElement: HTMLElement, targetElement: HTMLElement, updateScrollbar: () => void) => {
    if (isScrollingRef.current) return;
    isScrollingRef.current = true;

    targetElement.scrollLeft = sourceElement.scrollLeft;
    updateScrollbar();

    requestAnimationFrame(() => {
      isScrollingRef.current = false;
    });
  }, []);

  // ========== 横スクロールバー ==========
  const customScrollThumbRef = useRef<HTMLDivElement>(null);
  const customScrollTrackRef = useRef<HTMLDivElement>(null);

  const [scrollThumbPosition, setScrollThumbPosition] = useState(0);
  const [scrollThumbWidth, setScrollThumbWidth] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  /**
   * 横スクロールバーの更新
   */
  const updateHorizontalScrollbar = useCallback(() => {
    if (!headerContainerRef.current) return;

    const container = headerContainerRef.current;
    const scrollLeft = container.scrollLeft;
    const scrollWidth = container.scrollWidth;
    const clientWidth = container.clientWidth;

    if (scrollWidth <= clientWidth) {
      setScrollThumbWidth(0);
      setScrollThumbPosition(0);
      return;
    }

    const availableWidth = clientWidth - fixedColumnWidth;
    const contentWidth = scrollWidth - fixedColumnWidth;

    const thumbWidth = (availableWidth / contentWidth) * availableWidth;
    const thumbPosition = (scrollLeft / (scrollWidth - clientWidth)) * (availableWidth - thumbWidth);

    setScrollThumbWidth(thumbWidth);
    setScrollThumbPosition(thumbPosition);
  }, [fixedColumnWidth, headerContainerRef]);

  /**
   * ヘッダーのスクロールハンドラ
   */
  const handleHeaderScroll = useCallback(() => {
    if (headerContainerRef.current && dataScrollAreaRef.current) {
      syncScroll(headerContainerRef.current, dataScrollAreaRef.current, updateHorizontalScrollbar);
    }
  }, [syncScroll, updateHorizontalScrollbar, headerContainerRef, dataScrollAreaRef]);

  /**
   * 横スクロールバーのドラッグ処理
   */
  const handleHorizontalThumbMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);

    const startX = e.clientX;
    const startPosition = scrollThumbPosition;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - startX;
      const trackElement = customScrollTrackRef.current;
      const headerElement = headerContainerRef.current;

      if (!trackElement || !headerElement) return;

      const trackWidth = trackElement.clientWidth;
      const maxPosition = trackWidth - scrollThumbWidth;
      const newPosition = Math.max(0, Math.min(maxPosition, startPosition + deltaX));

      let scrollRatio = 0;
      if (trackWidth - scrollThumbWidth !== 0) {
        scrollRatio = newPosition / (trackWidth - scrollThumbWidth);
      }
      const maxScroll = headerElement.scrollWidth - headerElement.clientWidth;
      headerElement.scrollLeft = scrollRatio * maxScroll;

      if (dataScrollAreaRef.current) {
        dataScrollAreaRef.current.scrollLeft = headerElement.scrollLeft;
      }

      setScrollThumbPosition(newPosition);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [scrollThumbPosition, scrollThumbWidth, headerContainerRef, dataScrollAreaRef]);

  /**
   * 横スクロールバートラックのクリック処理
   */
  const handleHorizontalTrackClick = useCallback((e: React.MouseEvent) => {
    if (e.target !== customScrollTrackRef.current) return;

    const trackElement = customScrollTrackRef.current;
    const headerElement = headerContainerRef.current;

    if (!trackElement || !headerElement) return;

    const trackRect = trackElement.getBoundingClientRect();
    const clickPosition = e.clientX - trackRect.left;
    const trackWidth = trackElement.clientWidth;

    const newThumbPosition = Math.max(0, Math.min(trackWidth - scrollThumbWidth, clickPosition - scrollThumbWidth / 2));

    let scrollRatio = 0;
    const denominator = trackWidth - scrollThumbWidth;
    if (denominator !== 0) {
      scrollRatio = newThumbPosition / denominator;
    }
    const maxScroll = headerElement.scrollWidth - headerElement.clientWidth;
    headerElement.scrollLeft = scrollRatio * maxScroll;

    if (dataScrollAreaRef.current) {
      dataScrollAreaRef.current.scrollLeft = headerElement.scrollLeft;
    }

    updateHorizontalScrollbar();
  }, [scrollThumbWidth, updateHorizontalScrollbar, headerContainerRef, dataScrollAreaRef]);

  // ========== 縦スクロールバー ==========
  const verticalScrollThumbRef = useRef<HTMLDivElement>(null);
  const verticalScrollTrackRef = useRef<HTMLDivElement>(null);

  const [verticalScrollThumbPosition, setVerticalScrollThumbPosition] = useState(0);
  const [verticalScrollThumbHeight, setVerticalScrollThumbHeight] = useState(0);
  const [isVerticalDragging, setIsVerticalDragging] = useState(false);

  /**
   * 縦スクロールバーの更新
   */
  const updateVerticalScrollbar = useCallback(() => {
    if (!dataScrollAreaRef.current) return;

    const container = dataScrollAreaRef.current;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;

    if (scrollHeight <= clientHeight) {
      setVerticalScrollThumbHeight(0);
      setVerticalScrollThumbPosition(0);
      return;
    }

    const thumbHeight = (clientHeight / scrollHeight) * clientHeight;
    const thumbPosition = (scrollTop / (scrollHeight - clientHeight)) * (clientHeight - thumbHeight);

    setVerticalScrollThumbHeight(thumbHeight);
    setVerticalScrollThumbPosition(thumbPosition);
  }, [dataScrollAreaRef]);

  /**
   * データのスクロールハンドラ
   */
  const handleDataScroll = useCallback(() => {
    if (dataScrollAreaRef.current && headerContainerRef.current) {
      syncScroll(dataScrollAreaRef.current, headerContainerRef.current, updateHorizontalScrollbar);
      updateVerticalScrollbar();
    }
  }, [syncScroll, updateHorizontalScrollbar, updateVerticalScrollbar, dataScrollAreaRef, headerContainerRef]);

  /**
   * 縦スクロールバーのドラッグ処理
   */
  const handleVerticalThumbMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsVerticalDragging(true);

    const startY = e.clientY;
    const startPosition = verticalScrollThumbPosition;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaY = e.clientY - startY;
      const trackElement = verticalScrollTrackRef.current;
      const dataElement = dataScrollAreaRef.current;

      if (!trackElement || !dataElement) return;

      const trackHeight = trackElement.clientHeight;
      const maxPosition = trackHeight - verticalScrollThumbHeight;
      const newPosition = Math.max(0, Math.min(maxPosition, startPosition + deltaY));

      const denominator = trackHeight - verticalScrollThumbHeight;
      const scrollRatio = denominator !== 0 ? newPosition / denominator : 0;
      const maxScroll = dataElement.scrollHeight - dataElement.clientHeight;
      dataElement.scrollTop = scrollRatio * maxScroll;

      setVerticalScrollThumbPosition(newPosition);
    };

    const handleMouseUp = () => {
      setIsVerticalDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [verticalScrollThumbPosition, verticalScrollThumbHeight, dataScrollAreaRef]);

  /**
   * 縦スクロールバートラックのクリック処理
   */
  const handleVerticalTrackClick = useCallback((e: React.MouseEvent) => {
    if (e.target !== verticalScrollTrackRef.current) return;

    const trackElement = verticalScrollTrackRef.current;
    const dataElement = dataScrollAreaRef.current;

    if (!trackElement || !dataElement) return;

    const trackRect = trackElement.getBoundingClientRect();
    const clickPosition = e.clientY - trackRect.top;
    const trackHeight = trackElement.clientHeight;

    const newThumbPosition = Math.max(0, Math.min(trackHeight - verticalScrollThumbHeight, clickPosition - verticalScrollThumbHeight / 2));

    const denominator = trackHeight - verticalScrollThumbHeight;
    const scrollRatio = denominator !== 0 ? newThumbPosition / denominator : 0;
    const maxScroll = dataElement.scrollHeight - dataElement.clientHeight;
    dataElement.scrollTop = scrollRatio * maxScroll;

    updateVerticalScrollbar();
  }, [verticalScrollThumbHeight, updateVerticalScrollbar, dataScrollAreaRef]);

  // ========== マウスホイール処理 ==========
  /**
   * マウスホイールでの横スクロールハンドラ
   */
  const handleWheelNative = useCallback((e: WheelEvent, isHeader: boolean) => {
    if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
      if (e.cancelable) {
        e.preventDefault();
      }

      const scrollAmount = e.deltaX !== 0 ? e.deltaX : e.deltaY;
      const targetRef = isHeader ? headerContainerRef : dataScrollAreaRef;
      const otherRef = isHeader ? dataScrollAreaRef : headerContainerRef;

      if (targetRef.current && otherRef.current) {
        const newScrollLeft = targetRef.current.scrollLeft + scrollAmount;
        targetRef.current.scrollLeft = newScrollLeft;
        otherRef.current.scrollLeft = newScrollLeft;
      }
    }
  }, [headerContainerRef, dataScrollAreaRef]);

  /**
   * イベントリスナーを設定
   */
  useEffect(() => {
    const headerElement = headerContainerRef.current;
    const dataElement = dataScrollAreaRef.current;

    if (headerElement && dataElement) {
      const headerWheelHandler = (e: WheelEvent) => handleWheelNative(e, true);
      const dataWheelHandler = (e: WheelEvent) => handleWheelNative(e, false);

      headerElement.addEventListener('wheel', headerWheelHandler, { passive: false });
      dataElement.addEventListener('wheel', dataWheelHandler, { passive: false });

      return () => {
        headerElement.removeEventListener('wheel', headerWheelHandler);
        dataElement.removeEventListener('wheel', dataWheelHandler);
      };
    }
  }, [handleWheelNative, headerContainerRef, dataScrollAreaRef]);

  /**
   * スクロールバーを更新（リサイズやデータ変更時に呼び出す）
   */
  const updateScrollbars = useCallback(() => {
    updateHorizontalScrollbar();
    updateVerticalScrollbar();
  }, [updateHorizontalScrollbar, updateVerticalScrollbar]);

  return {
    // 横スクロールバー
    horizontal: {
      thumbRef: customScrollThumbRef,
      trackRef: customScrollTrackRef,
      thumbPosition: scrollThumbPosition,
      thumbWidth: scrollThumbWidth,
      isDragging,
      handleScroll: handleHeaderScroll,
      handleThumbMouseDown: handleHorizontalThumbMouseDown,
      handleTrackClick: handleHorizontalTrackClick,
      update: updateHorizontalScrollbar
    },
    // 縦スクロールバー
    vertical: {
      thumbRef: verticalScrollThumbRef,
      trackRef: verticalScrollTrackRef,
      thumbPosition: verticalScrollThumbPosition,
      thumbHeight: verticalScrollThumbHeight,
      isDragging: isVerticalDragging,
      handleScroll: handleDataScroll,
      handleThumbMouseDown: handleVerticalThumbMouseDown,
      handleTrackClick: handleVerticalTrackClick,
      update: updateVerticalScrollbar
    },
    // 共通
    updateScrollbars
  };
};
